import React, { useState, useEffect, useMemo } from 'react';
import VideoPopup from './components/VideoPopup';
import logoImage from './assets/images/logo.PNG';
import olivioVideo from './assets/thumbnails/Olivio.mp4';
import mediCareVideo from './assets/thumbnails/MediCare.mp4';
import customerSupportVideo from './assets/thumbnails/CustomerSupport.mp4';
import conciergeVideo from './assets/thumbnails/Concierge.mp4';
import realEstateVideo from './assets/thumbnails/RealEstateAssistant.mp4';
import olivioPoster from './assets/thumbnails/Olivio-poster.jpg';
import mediCarePoster from './assets/thumbnails/MediCare-poster.jpg';
import customerSupportPoster from './assets/thumbnails/CustomerSupport-poster.jpg';
import conciergePoster from './assets/thumbnails/Concierge-poster.jpg';
// Real estate poster will use video as fallback if poster doesn't exist


const OlivioAIAgency = () => {
  const [chatVisible, setChatVisible] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [headerBackground, setHeaderBackground] = useState('rgba(0, 0, 0, 0.8)');
  const [showVideoPopup, setShowVideoPopup] = useState(false);
  const [isVideoChat, setIsVideoChat] = useState(false);

  // Bot selection state
  const [selectedBot, setSelectedBot] = useState('olivio'); // 'olivio', 'healthcare', 'support', 'hospitality', 'realestate'
  const [carouselIndex, setCarouselIndex] = useState(0); // Current carousel position

  // Language state
  const [language, setLanguage] = useState('en'); // 'en' or 'de'

  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Translations
  const t = {
    en: {
      // Header
      home: 'Home',
      services: 'Services',
      industries: 'Industries',
      contact: 'Contact',
      getInTouch: 'Get in Touch',

      // Hero
      heroBadge: 'AI-Powered Voice & Video Automation',
      heroTitle: 'More Revenue Through Hyper-Effective AI',
      heroSubtitle: 'Transform your customer interactions with cutting-edge AI voice and video technology. Automate conversations, qualify leads, and scale your business effortlessly.',
      talkToOlivio: 'Talk to our AI Assistants',
      viewServices: 'View Services',
      startConversation: 'Start Conversation',

      // Services
      ourServices: 'Our Services',
      servicesTitle: 'AI Solutions That Take Your Business to the Next Level',
      servicesSubtitle: 'We design, develop, and implement automation tools that help you work smarter, not harder.',

      customAI: 'Custom AI Assistants',
      customAIDesc: 'Tailored conversational Video/Voice AI solutions for your specific business needs, with personalized digital personas and advanced reasoning capabilities.',

      videoAI: 'Video AI Integration',
      videoAIDesc: 'Seamless integration of realistic video AI technology into your existing platforms and workflows for enhanced user engagement.',

      aiOrchestration: 'AI Orchestration',
      aiOrchestrationDesc: 'Advanced AI orchestration services using cutting-edge frameworks for real-time, multi-modal AI interactions.',

      aiAnalytics: 'AI Agents',
      aiAnalyticsDesc: 'Intelligent AI agents that handle complex tasks autonomously, from customer service to data processing and beyond.',

      enterpriseSecurity: 'Enterprise Security',
      enterpriseSecurityDesc: 'Enterprise-grade security and privacy protection for all AI interactions and data processing.',

      support247: '24/7 Support',
      support247Desc: 'Round-the-clock technical support and maintenance for all your AI systems and integrations.',

      // Workflow
      howWeWork: 'How We Work',
      workflowTitle: 'Your Journey to AI-Powered Success',
      workflowStep1Title: 'Discovery Call',
      workflowStep1Desc: 'We dive deep into your business challenges and explore how our AI voice and video agents can transform your customer interactions and boost revenue.',
      workflowStep2Title: 'Strategy & Design',
      workflowStep2Desc: 'Our team creates a custom AI solution blueprint tailored to your industry, complete with persona design and conversation flows.',
      workflowStep3Title: 'Development & Integration',
      workflowStep3Desc: 'We build and integrate your AI agents using cutting-edge technology, ensuring seamless connection with your existing systems and workflows.',
      workflowStep4Title: 'Testing & Refinement',
      workflowStep4Desc: 'Through rigorous testing and optimization, we fine-tune your AI agents to deliver exceptional performance and natural interactions.',
      workflowStep5Title: 'Launch & Scale',
      workflowStep5Desc: 'Your AI agents go live, start engaging customers 24/7, and we provide ongoing support to help you scale and maximize ROI.',
      integrateAI: 'Start Your AI Transformation Today',

      // Industries
      industrySolutions: 'Industry Solutions',
      industryTitle: 'Tailored for Your Industry',
      industrySubtitle: 'Video & Voice AI agents designed specifically for your business sector',

      // Industry Cards
      realEstate: 'Real Estate',
      realEstateDesc: 'Deploy AI-powered virtual assistants that conduct property consultations, qualify leads through natural conversation, and provide 24/7 property information.',
      realEstateFeature1: 'Virtual property consultations with face-to-face AI agents',
      realEstateFeature2: 'Voice-activated property search and filtering',
      realEstateFeature3: 'Automated lead qualification through conversational AI',
      realEstateFeature4: 'Multilingual video tours with AI-powered narration',

      ecommerce: 'E-commerce',
      ecommerceDesc: 'Transform online shopping with conversational video assistants that guide customers through product selections and answer questions in real-time.',
      ecommerceFeature1: 'Interactive video product demonstrations',
      ecommerceFeature2: 'Voice-powered product search and comparison',
      ecommerceFeature3: 'Real-time shopping assistance via video chat',
      ecommerceFeature4: 'Personalized style consultations with AI avatars',

      influencer: 'Influencer & Streamer',
      influencerDesc: 'Scale your content creation and audience engagement with AI-powered digital twins that interact with your community 24/7.',
      influencerFeature1: 'AI avatar that mimics your personality and voice',
      influencerFeature2: 'Automated fan interactions and Q&A sessions',
      influencerFeature3: 'Multi-platform streaming with AI co-hosts',
      influencerFeature4: 'Personalized content recommendations for followers',

      customerService: 'Customer Service',
      customerServiceDesc: 'Revolutionize support operations with AI agents that handle complex inquiries through video calls and provide visual troubleshooting guidance.',
      customerServiceFeature1: 'Face-to-face video support sessions',
      customerServiceFeature2: 'Voice-activated ticket creation and status updates',
      customerServiceFeature3: 'Interactive visual troubleshooting guides',
      customerServiceFeature4: 'Seamless escalation to human agents',

      healthcare: 'Healthcare',
      healthcareDesc: 'Improve patient care with AI assistants that conduct preliminary consultations and provide health education through interactive video.',
      healthcareFeature1: 'Video-based symptom assessment and triage',
      healthcareFeature2: 'Voice-activated appointment scheduling and reminders',
      healthcareFeature3: 'Interactive health education and wellness coaching',
      healthcareFeature4: 'Multilingual patient support and communication',

      hospitality: 'Hospitality',
      hospitalityDesc: 'Elevate guest experiences with AI concierges available via video calls and voice commands throughout the property.',
      hospitalityFeature1: '24/7 video concierge services',
      hospitalityFeature2: 'Voice-controlled room amenities and services',
      hospitalityFeature3: 'Interactive local area guides and recommendations',
      hospitalityFeature4: 'Real-time multilingual guest support',

      // Contact
      contactLabel: 'Get In Touch',
      contactTitle: 'Ready to Transform Your Business?',
      contactSubtitle: 'Let\'s discuss how AI can revolutionize your customer interactions',

      fullName: 'Full Name',
      emailAddress: 'Email Address',
      company: 'Company',
      phoneNumber: 'Phone Number',
      message: 'Message',

      namePlaceholder: 'Your full name',
      emailPlaceholder: 'your.email@company.com',
      companyPlaceholder: 'Your company name',
      phonePlaceholder: '+1 (555) 123-4567',
      messagePlaceholder: 'Tell us about your project, requirements, and how we can help you...',

      sendMessage: 'Send Message',
      sending: 'Sending...',

      // Footer
      allRightsReserved: 'All rights reserved.',

      // Video Popup
      olivioAIAssistant: 'Olivio AI Assistant',
      olivioWaiting: 'Olivio is waiting for you',
      checkYourMic: 'Check your mic, so Olivio can hear you!',
      defaultMicrophone: 'Default Microphone',
      chooseMicrophone: 'Choose Microphone',
      microphone: 'Microphone',
      appointmentConfirmed: 'Your appointment is confirmed!',
      lookForwardToMeeting: 'We look forward to meeting with you',
      clientIntakeForm: 'Client Intake Form',
      userInformation: 'User Information',
      nameLabel: 'Name',
      companyLabel: 'Company',
      industryLabel: 'Industry',
      stageLabel: 'Stage',
      appointmentInformation: 'Appointment Information',
      dateLabel: 'Date',
      timeLabel: 'Time',
      emailLabel: 'Email',
      disconnect: 'Disconnect',

      // Notifications
      success: 'Success!',
      error: 'Error',
      requiredFields: 'Please fill in all required fields (Name, Email, and Message).',
      validEmail: 'Please enter a valid email address.',
      tooManySubmissions: 'Too many submissions. Please try again in an hour.',
      thankYou: 'Thank you for your message! We\'ll get back to you soon.',
      checkForm: 'Please check your form and try again.',
      accessDenied: 'Access denied. Please refresh the page and try again.',
      serviceNotFound: 'Service not found. Please contact us at contact@pro-avatar-strategy.com',
      serverError: 'Server error. Please try again later or contact us at contact@pro-avatar-strategy.com',
      serviceNotImplemented: 'Service not implemented. Please contact us at contact@pro-avatar-strategy.com',
      serviceUnavailable: 'Service temporarily unavailable. Please try again later or contact us at contact@pro-avatar-strategy.com',
      serverTimeout: 'Server timeout. Please try again later or contact us at contact@pro-avatar-strategy.com',
    },
    de: {
      // Header
      home: 'Startseite',
      services: 'Dienstleistungen',
      industries: 'Branchen',
      contact: 'Kontakt',
      getInTouch: 'Kontaktieren Sie uns',

      // Hero
      heroBadge: 'KI-gestützte Sprach- & Videoautomatisierung',
      heroTitle: 'Mehr Umsatz durch hocheffektive KI',
      heroSubtitle: 'Transformieren Sie Ihre Kundeninteraktionen mit modernster KI-Sprach- und Videotechnologie. Automatisieren Sie Gespräche, qualifizieren Sie Leads und skalieren Sie Ihr Geschäft mühelos.',
      talkToOlivio: 'Sprechen Sie mit unseren KI-Assistenten',
      viewServices: 'Dienstleistungen ansehen',
      startConversation: 'Gespräch beginnen',

      // Services
      ourServices: 'Unsere Dienstleistungen',
      servicesTitle: 'KI-Lösungen, die Ihr Unternehmen auf die nächste Stufe heben',
      servicesSubtitle: 'Wir entwerfen, entwickeln und implementieren Automatisierungstools, die Ihnen helfen, intelligenter zu arbeiten, nicht härter.',

      customAI: 'Maßgeschneiderte KI-Assistenten',
      customAIDesc: 'Maßgeschneiderte konversationelle Video-/Sprach-KI-Lösungen für Ihre spezifischen Geschäftsanforderungen, mit personalisierten digitalen Personas und fortgeschrittenen Reasoning-Fähigkeiten.',

      videoAI: 'Video-KI-Integration',
      videoAIDesc: 'Nahtlose Integration realistischer Video-KI-Technologie in Ihre bestehenden Plattformen und Workflows für verbesserte Nutzerbindung.',

      aiOrchestration: 'KI-Orchestrierung',
      aiOrchestrationDesc: 'Fortgeschrittene KI-Orchestrierungsdienste mit modernsten Frameworks für Echtzeit-Multi-Modal-KI-Interaktionen.',

      aiAnalytics: 'KI-Analytik',
      aiAnalyticsDesc: 'Umfassende Analysen und Einblicke aus Ihren KI-Interaktionen zur Optimierung von Leistung und Nutzererfahrung.',

      enterpriseSecurity: 'Unternehmenssicherheit',
      enterpriseSecurityDesc: 'Sicherheit und Datenschutz auf Unternehmensebene für alle KI-Interaktionen und Datenverarbeitung.',

      support247: '24/7 Support',
      support247Desc: 'Rund-um-die-Uhr technischer Support und Wartung für alle Ihre KI-Systeme und Integrationen.',

      // Workflow
      howWeWork: 'So arbeiten wir',
      workflowTitle: 'So läuft die Zusammenarbeit mit uns ab',
      workflowStep1Title: 'Individuelle Beratung',
      workflowStep1Desc: 'Wir analysieren deine Situation und zeigen dir, wie ein KI-System dich optimal unterstützt.',
      workflowStep2Title: 'Onboarding',
      workflowStep2Desc: 'Wir klären alle technischen Details, damit dein KI-System reibungslos konfiguriert werden kann.',
      workflowStep3Title: 'Entwicklung',
      workflowStep3Desc: 'Wir entwickeln dir das vereinbarte KI-System passgenau für deine Bedürfnisse – mit präzisem Prompt Engineering für optimale Ergebnisse.',
      workflowStep4Title: 'Optimierung',
      workflowStep4Desc: 'Dein KI-System wird getestet, analysiert und optimiert – für maximale Performance. Es wird alles vorbereitet für den Live Betrieb.',
      workflowStep5Title: 'Live Betrieb',
      workflowStep5Desc: 'Dein KI-System geht Live und steigert ab Tag 1 deine Produktivität und Effizienz.',
      integrateAI: 'KI-Systeme jetzt in dein Business einbinden',

      // Industries
      industrySolutions: 'Branchenlösungen',
      industryTitle: 'Maßgeschneidert für Ihre Branche',
      industrySubtitle: 'Video- und Sprach-KI-Agenten, die speziell für Ihren Geschäftsbereich entwickelt wurden',

      // Industry Cards
      realEstate: 'Immobilien',
      realEstateDesc: 'Setzen Sie KI-gestützte virtuelle Assistenten ein, die Immobilienberatungen durchführen, Leads durch natürliche Konversation qualifizieren und rund um die Uhr Immobilieninformationen bereitstellen.',
      realEstateFeature1: 'Virtuelle Immobilienberatungen mit persönlichen KI-Agenten',
      realEstateFeature2: 'Sprachaktivierte Immobiliensuche und Filterung',
      realEstateFeature3: 'Automatisierte Lead-Qualifizierung durch konversationelle KI',
      realEstateFeature4: 'Mehrsprachige Videoführungen mit KI-gestützter Moderation',

      ecommerce: 'E-Commerce',
      ecommerceDesc: 'Transformieren Sie Online-Shopping mit konversationellen Video-Assistenten, die Kunden durch Produktauswahl führen und Fragen in Echtzeit beantworten.',
      ecommerceFeature1: 'Interaktive Video-Produktvorführungen',
      ecommerceFeature2: 'Sprachgesteuerte Produktsuche und Vergleich',
      ecommerceFeature3: 'Echtzeit-Einkaufsberatung per Video-Chat',
      ecommerceFeature4: 'Personalisierte Stilberatung mit KI-Avataren',

      influencer: 'Influencer & Streamer',
      influencerDesc: 'Skalieren Sie Ihre Content-Erstellung und Publikumsbindung mit KI-gestützten digitalen Zwillingen, die rund um die Uhr mit Ihrer Community interagieren.',
      influencerFeature1: 'KI-Avatar, der Ihre Persönlichkeit und Stimme nachahmt',
      influencerFeature2: 'Automatisierte Fan-Interaktionen und Q&A-Sessions',
      influencerFeature3: 'Multiplattform-Streaming mit KI-Co-Hosts',
      influencerFeature4: 'Personalisierte Content-Empfehlungen für Follower',

      customerService: 'Kundenservice',
      customerServiceDesc: 'Revolutionieren Sie Support-Operationen mit KI-Agenten, die komplexe Anfragen über Videoanrufe bearbeiten und visuelle Fehlerbehebungsanleitungen bereitstellen.',
      customerServiceFeature1: 'Persönliche Video-Support-Sitzungen',
      customerServiceFeature2: 'Sprachaktivierte Ticket-Erstellung und Statusaktualisierungen',
      customerServiceFeature3: 'Interaktive visuelle Fehlerbehebungsanleitungen',
      customerServiceFeature4: 'Nahtlose Eskalation zu menschlichen Agenten',

      healthcare: 'Gesundheitswesen',
      healthcareDesc: 'Verbessern Sie die Patientenversorgung mit KI-Assistenten, die Voruntersuchungen durchführen und Gesundheitsaufklärung durch interaktives Video bieten.',
      healthcareFeature1: 'Videobasierte Symptombewertung und Triage',
      healthcareFeature2: 'Sprachaktivierte Terminplanung und Erinnerungen',
      healthcareFeature3: 'Interaktive Gesundheitserziehung und Wellness-Coaching',
      healthcareFeature4: 'Mehrsprachige Patientenunterstützung und Kommunikation',

      hospitality: 'Gastgewerbe',
      hospitalityDesc: 'Verbessern Sie das Gästeerlebnis mit KI-Concierges, die über Videoanrufe und Sprachbefehle im gesamten Objekt verfügbar sind.',
      hospitalityFeature1: '24/7 Video-Concierge-Services',
      hospitalityFeature2: 'Sprachgesteuerte Zimmerausstattung und -dienstleistungen',
      hospitalityFeature3: 'Interaktive lokale Reiseführer und Empfehlungen',
      hospitalityFeature4: 'Echtzeit mehrsprachiger Gästesupport',

      // Contact
      contactLabel: 'Kontakt aufnehmen',
      contactTitle: 'Bereit, Ihr Geschäft zu transformieren?',
      contactSubtitle: 'Lassen Sie uns besprechen, wie KI Ihre Kundeninteraktionen revolutionieren kann',

      fullName: 'Vollständiger Name',
      emailAddress: 'E-Mail-Adresse',
      company: 'Unternehmen',
      phoneNumber: 'Telefonnummer',
      message: 'Nachricht',

      namePlaceholder: 'Ihr vollständiger Name',
      emailPlaceholder: 'ihre.email@unternehmen.de',
      companyPlaceholder: 'Ihr Unternehmensname',
      phonePlaceholder: '+49 (123) 456-7890',
      messagePlaceholder: 'Erzählen Sie uns von Ihrem Projekt, Ihren Anforderungen und wie wir Ihnen helfen können...',

      sendMessage: 'Nachricht senden',
      sending: 'Wird gesendet...',

      // Footer
      allRightsReserved: 'Alle Rechte vorbehalten.',

      // Video Popup
      olivioAIAssistant: 'Olivio KI-Assistent',
      olivioWaiting: 'Olivio wartet auf Sie',
      checkYourMic: 'Überprüfen Sie Ihr Mikrofon, damit Olivio Sie hören kann!',
      defaultMicrophone: 'Standard-Mikrofon',
      chooseMicrophone: 'Mikrofon wählen',
      microphone: 'Mikrofon',
      appointmentConfirmed: 'Ihr Termin ist bestätigt!',
      lookForwardToMeeting: 'Wir freuen uns darauf, Sie zu treffen',
      clientIntakeForm: 'Kunden-Aufnahmeformular',
      userInformation: 'Benutzerinformationen',
      nameLabel: 'Name',
      companyLabel: 'Unternehmen',
      industryLabel: 'Branche',
      stageLabel: 'Phase',
      appointmentInformation: 'Termininformationen',
      dateLabel: 'Datum',
      timeLabel: 'Uhrzeit',
      emailLabel: 'E-Mail',
      disconnect: 'Trennen',

      // Notifications
      success: 'Erfolg!',
      error: 'Fehler',
      requiredFields: 'Bitte füllen Sie alle erforderlichen Felder aus (Name, E-Mail und Nachricht).',
      validEmail: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
      tooManySubmissions: 'Zu viele Einreichungen. Bitte versuchen Sie es in einer Stunde erneut.',
      thankYou: 'Vielen Dank für Ihre Nachricht! Wir melden uns bald bei Ihnen.',
      checkForm: 'Bitte überprüfen Sie Ihr Formular und versuchen Sie es erneut.',
      accessDenied: 'Zugriff verweigert. Bitte aktualisieren Sie die Seite und versuchen Sie es erneut.',
      serviceNotFound: 'Dienst nicht gefunden. Bitte kontaktieren Sie uns unter contact@pro-avatar-strategy.com',
      serverError: 'Serverfehler. Bitte versuchen Sie es später erneut oder kontaktieren Sie uns unter contact@pro-avatar-strategy.com',
      serviceNotImplemented: 'Dienst nicht implementiert. Bitte kontaktieren Sie uns unter contact@pro-avatar-strategy.com',
      serviceUnavailable: 'Dienst vorübergehend nicht verfügbar. Bitte versuchen Sie es später erneut oder kontaktieren Sie uns unter contact@pro-avatar-strategy.com',
      serverTimeout: 'Server-Timeout. Bitte versuchen Sie es später erneut oder kontaktieren Sie uns unter contact@pro-avatar-strategy.com',
    }
  };

  // Get current translations
  const tr = t[language];

  // Bot configurations - memoized to prevent re-creation on every render
  const bots = useMemo(() => ({
    olivio: {
      id: 'olivio',
      configName: 'olivio_sales',  // Maps to olivio_sales.yaml
      name: 'Pro Avatar Strategy Assistant',
      personaName: 'Anastasia',  // Persona name from bot config
      description: 'Your company AI assistant for business strategy',
      icon: 'fa-robot',
      color: '#ffb84d',
      video: olivioVideo,
      poster: olivioPoster,
      showSidePanel: true,  // Show side panel with controls
    },
    healthcare: {
      id: 'healthcare',
      configName: 'medicare_assistant',  // Maps to medicare_assistant.yaml
      name: 'MediCare AI Assistant',
      personaName: 'Dr. Maya',  // Persona name from bot config
      description: 'Healthcare consultation and patient support',
      icon: 'fa-heartbeat',
      color: '#10b981',
      video: mediCareVideo,
      poster: mediCarePoster,
      showSidePanel: true,  // Show side panel with controls
    },
    realestate: {
      id: 'realestate',
      configName: 'real_estate_assistant',  // Maps to real_estate_assistant.yaml
      name: 'Real Estate AI Assistant',
      personaName: 'Sarah',  // Persona name from bot config
      description: 'Property search and viewing scheduling',
      icon: 'fa-home',
      color: '#f59e0b',
      video: realEstateVideo,
      poster: null,  // Will use video poster frame
      showSidePanel: false,  // Hide side panel - video only
    },
    hospitality: {
      id: 'hospitality',
      configName: 'concierge_assistant',  // Maps to concierge_assistant.yaml
      name: 'Concierge AI Assistant',
      personaName: 'Isabella',  // Persona name from bot config
      description: 'Hospitality and guest experience',
      icon: 'fa-concierge-bell',
      color: '#8b5cf6',
      video: conciergeVideo,
      poster: conciergePoster,
      showSidePanel: false,  // Hide side panel - video only
    },
    support: {
      id: 'support',
      configName: 'support_bot',  // Maps to support_bot.yaml
      name: 'SupportPro AI Assistant',
      personaName: 'Alex',  // Persona name from bot config
      description: 'Customer service and technical support',
      icon: 'fa-headset',
      color: '#3b82f6',
      video: customerSupportVideo,
      poster: customerSupportPoster,
      showSidePanel: true,  // Show side panel with controls
    }
  }), []); // Empty dependency array - bots never change

  // Memoize bot values array to prevent recreation
  const botsList = useMemo(() => Object.values(bots), [bots]);

  // Preload all showcase images on home page load
  useEffect(() => {
    const imagesToPreload = [
      // Concierge images (8 images)
      'assistants/concierge_assistant/classic_double_bedroom_hotel.webp',
      'assistants/concierge_assistant/junior_suite.webp',
      'assistants/concierge_assistant/luxury_suite_hotel.webp',
      'assistants/concierge_assistant/hotel_bar_and_restaurant.webp',
      'assistants/concierge_assistant/spa_wellness_entry.webp',
      'assistants/concierge_assistant/hotel_meeting_room.webp',
      'assistants/concierge_assistant/hotel_meeting_room2.webp',
      'assistants/concierge_assistant/hotel_conference_hall.webp',
      // Real estate images (13 images)
      'assistants/real_estates_assistant/property_type_penthouse.webp',
      'assistants/real_estates_assistant/property_type_villa.webp',
      'assistants/real_estates_assistant/property_type_family_home.webp',
      'assistants/real_estates_assistant/property_type_apartment.webp',
      'assistants/real_estates_assistant/real_estate_modern_villa.webp',
      'assistants/real_estates_assistant/real_estate_modern_villa_mountains.webp',
      'assistants/real_estates_assistant/real_estate_penthouse_apartment_city.webp',
      'assistants/real_estates_assistant/real_estate_penthouse_apartment_countryside.webp',
      'assistants/real_estates_assistant/real_estate_family_home_suburban.webp',
      'assistants/real_estates_assistant/real_estate_family_home_modern.webp',
      'assistants/real_estates_assistant/real_estate_family_home_traditional.webp',
      'assistants/real_estates_assistant/real_estate_apartment_city.webp',
      'assistants/real_estates_assistant/real_estate_apartment_luxury.webp',
    ];

    // Start preloading after initial page render
    const timer = setTimeout(() => {
      console.log(`Home page loaded - preloading ${imagesToPreload.length} showcase images`);
      imagesToPreload.forEach(src => {
        const img = new Image();
        img.src = `/images/${src}`;
      });
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Handle scroll effect for header
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setHeaderBackground('rgba(0, 0, 0, 0.95)');
      } else {
        setHeaderBackground('rgba(0, 0, 0, 0.8)');
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle escape key to close popup
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setShowVideoPopup(false);
      }
    };

    if (showVideoPopup) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [showVideoPopup]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      if (sectionId === 'bot-selection') {
        // Scroll to middle of bot-selection section
        const elementRect = element.getBoundingClientRect();
        const absoluteElementTop = elementRect.top + window.pageYOffset;
        const middle = absoluteElementTop + (element.offsetHeight / 2) - (window.innerHeight / 2);
        window.scrollTo({ top: middle, behavior: 'smooth' });
      } else {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  // Start video chat function
  const startVideoChat = () => {
    setIsVideoChat(true);
  };

  // Close popup and reset video chat state
  const closeVideoPopup = () => {
    setShowVideoPopup(false);
    setIsActive(false);
    setIsVideoChat(false);
  };

  // Start conversation function
  const startConversation = () => {
    setIsActive(true);
    setShowVideoPopup(true);
  };

  // Toggle chat interface
  const toggleChat = () => {
    setChatVisible(!chatVisible);
  };

  // Handle chat input
  const handleChatInput = (e) => {
    if (e.key === 'Enter') {
      const message = chatInput.trim();
      if (message) {
        addChatMessage('user', message);
        setChatInput('');

        setTimeout(() => {
          addChatMessage('ai', 'This is a demo response. Connect to your Pipecat backend for real AI interactions!');
        }, 1000);
      }
    }
  };

  // Add chat message
  const addChatMessage = (sender, message) => {
    setChatMessages(prev => [...prev, { sender, message, id: Date.now() }]);
  };

  // Handle contact form input
  const handleContactInputChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Show notification
  const showNotification = (message, type = 'success') => {
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      min-width: 300px;
      max-width: 500px;
      padding: 20px 24px;
      background: ${type === 'success' ? 'linear-gradient(135deg, #10b981, #059669)' : 'linear-gradient(135deg, #ef4444, #dc2626)'};
      color: white;
      border-radius: 12px;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
      z-index: 10000;
      font-family: 'Inter', sans-serif;
      font-size: 15px;
      line-height: 1.5;
      animation: slideIn 0.3s ease-out;
      display: flex;
      align-items: start;
      gap: 12px;
    `;

    notification.innerHTML = `
      <i class="fas fa-${type === 'success' ? 'check-circle' : 'exclamation-circle'}" style="font-size: 24px; margin-top: 2px;"></i>
      <div style="flex: 1;">
        <div style="font-weight: 600; margin-bottom: 4px;">${type === 'success' ? tr.success : tr.error}</div>
        <div style="opacity: 0.95;">${message}</div>
      </div>
      <button onclick="this.parentElement.remove()" style="background: none; border: none; color: white; cursor: pointer; font-size: 20px; padding: 0; opacity: 0.8; transition: opacity 0.2s;">
        <i class="fas fa-times"></i>
      </button>
    `;

    document.body.appendChild(notification);

    // Auto remove after 5 seconds
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease-in';
      setTimeout(() => notification.remove(), 300);
    }, 5000);

    // Add animations
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn {
        from {
          transform: translateX(400px);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
      @keyframes slideOut {
        from {
          transform: translateX(0);
          opacity: 1;
        }
        to {
          transform: translateX(400px);
          opacity: 0;
        }
      }
    `;
    if (!document.querySelector('[data-notification-styles]')) {
      style.setAttribute('data-notification-styles', 'true');
      document.head.appendChild(style);
    }
  };

  // Handle contact form submission
  const handleContactSubmit = async () => {
    // Validate required fields
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      showNotification(tr.requiredFields, 'error');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(contactForm.email)) {
      showNotification(tr.validEmail, 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      // Get backend URL from environment variable
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      if (!backendUrl) {
        showNotification('Backend URL is not configured. Please contact administrator.', 'error');
        setIsSubmitting(false);
        return;
      }

      const url = `${backendUrl}/submit-contact`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactForm),
      });

      // Handle different response codes
      if (response.status === 429) {
        showNotification(tr.tooManySubmissions, 'error');
        setIsSubmitting(false);
        return;
      }

      let data;
      try {
        data = await response.json();
      } catch (e) {
        data = { detail: tr.serverError };
      }

      if (response.ok) {
        showNotification(data.message || tr.thankYou, 'success');
        // Clear form on success
        setContactForm({
          name: '',
          email: '',
          company: '',
          phone: '',
          message: ''
        });
      } else if (response.status === 400) {
        showNotification(data.detail || tr.checkForm, 'error');
      } else if (response.status === 403) {
        showNotification(tr.accessDenied, 'error');
      } else if (response.status === 404) {
        showNotification(tr.serviceNotFound, 'error');
      } else if (response.status === 500) {
        showNotification(data.detail || tr.serverError, 'error');
      } else if (response.status === 501) {
        showNotification(tr.serviceNotImplemented, 'error');
      } else if (response.status === 502) {
        showNotification(tr.serverError, 'error');
      } else if (response.status === 503) {
        showNotification(tr.serviceUnavailable, 'error');
      } else if (response.status === 504) {
        showNotification(tr.serverTimeout, 'error');
      } else {
        showNotification(data.detail || tr.serverError, 'error');
      }
    } catch (error) {
      showNotification(tr.serverError, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          line-height: 1.6;
          color: #ffffff;
          background: #000000;
          min-height: 100vh;
          overflow-x: hidden;
          position: relative;
        }

        body::before {
          content: '';
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background:
            radial-gradient(ellipse at 50% 20%, rgba(255, 184, 77, 0.2) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 70%, rgba(255, 184, 77, 0.15) 0%, transparent 50%),
            radial-gradient(1px 1px at 20px 30px, white, transparent),
            radial-gradient(1px 1px at 60px 70px, white, transparent),
            radial-gradient(1px 1px at 50px 50px, white, transparent),
            radial-gradient(1px 1px at 130px 80px, white, transparent),
            radial-gradient(1px 1px at 80px 10px, white, transparent);
          background-size:
            100% 100%,
            100% 100%,
            200px 200px,
            200px 200px,
            200px 200px,
            200px 200px,
            200px 200px;
          animation: starfield 200s linear infinite;
          pointer-events: none;
          z-index: 0;
          opacity: 0.5;
        }

        @keyframes starfield {
          from {
            transform: translateY(0);
          }
          to {
            transform: translateY(-200px);
          }
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
          position: relative;
          z-index: 1;
        }

        /* Header */
        .header {
          position: fixed;
          top: 0;
          width: 100%;
          z-index: 1000;
          backdrop-filter: blur(10px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          transition: background 0.3s ease;
        }

        .nav {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.2rem 0;
        }

        .nav-right {
          display: flex;
          align-items: center;
          gap: 3rem;
        }

        .logo {
          font-size: 1.5rem;
          font-weight: 700;
          color: white;
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 12px;
          letter-spacing: -0.5px;
        }

        .logo-image {
          width: 36px;
          height: auto;
          max-height: 36px;
          object-fit: contain;
          border-radius: 4px;
        }

        .nav-links {
          display: flex;
          list-style: none;
          gap: 2.5rem;
        }

        .nav-link {
          color: rgba(255, 255, 255, 0.8);
          text-decoration: none;
          transition: color 0.3s ease;
          font-weight: 500;
          cursor: pointer;
          font-size: 0.95rem;
        }

        .nav-link:hover {
          color: #ffffff;
        }

        /* Orange CTA Button */
        .sales-button {
          position: relative;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 24px;
          background: linear-gradient(135deg, #ffb84d, #f59e0b);
          color: #1a1a1a;
          text-decoration: none;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.9rem;
          transition: all 0.3s ease;
          box-shadow: 0 4px 16px rgba(255, 184, 77, 0.4);
          cursor: pointer;
          border: none;
          white-space: nowrap;
        }

        .sales-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 24px rgba(255, 184, 77, 0.6);
          background: linear-gradient(135deg, #fbbf24, #ffb84d);
        }

        .sales-button i {
          font-size: 1rem;
          color: #ffffff;
        }

        .language-switcher {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          color: rgba(255, 255, 255, 0.8);
          font-weight: 600;
          font-size: 0.85rem;
          cursor: pointer;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        .language-switcher:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 184, 77, 0.5);
          color: white;
          transform: translateY(-2px);
        }

        .language-switcher i {
          font-size: 1rem;
          color: #ffb84d;
        }

        .header-social-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          color: rgba(255, 255, 255, 0.7);
          text-decoration: none;
          transition: all 0.3s ease;
          font-size: 1.2rem;
        }

        .header-social-link:hover {
          background: linear-gradient(135deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888);
          border-color: transparent;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 4px 16px rgba(240, 148, 51, 0.4);
        }

        /* Hero Section */
        .hero {
          padding: 180px 0 100px;
          text-align: center;
          position: relative;
          z-index: 1;
        }

        .hero::before {
          content: '';
          position: absolute;
          top: 10%;
          left: 50%;
          transform: translateX(-50%);
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, rgba(255, 184, 77, 0.3) 0%, transparent 70%);
          filter: blur(100px);
          pointer-events: none;
          z-index: -1;
        }

        .hero-badge {
          display: inline-block;
          padding: 8px 16px;
          background: rgba(255, 184, 77, 0.2);
          border: 1px solid rgba(255, 184, 77, 0.3);
          border-radius: 20px;
          color: #fcd34d;
          font-size: 0.85rem;
          font-weight: 600;
          margin-bottom: 2rem;
        }

        .hero h1 {
          font-size: 4rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
          color: #ffffff;
          line-height: 1.2;
          letter-spacing: -1px;
        }

        .hero p {
          font-size: 1.2rem;
          margin-bottom: 3rem;
          color: rgba(255, 255, 255, 0.7);
          max-width: 650px;
          margin-left: auto;
          margin-right: auto;
        }

        .cta-buttons {
          display: flex;
          gap: 1rem;
          justify-content: center;
          align-items: center;
        }

        .cta-button {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 32px;
          background: linear-gradient(135deg, #ffb84d, #f59e0b);
          color: white;
          text-decoration: none;
          border-radius: 8px;
          font-weight: 600;
          font-size: 1rem;
          transition: all 0.3s ease;
          box-shadow: 0 4px 16px rgba(255, 184, 77, 0.4);
          border: none;
          cursor: pointer;
        }

        .cta-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 24px rgba(255, 184, 77, 0.6);
        }

        .cta-button-secondary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 32px;
          background: rgba(255, 255, 255, 0.05);
          color: white;
          text-decoration: none;
          border-radius: 8px;
          font-weight: 600;
          font-size: 1rem;
          transition: all 0.3s ease;
          border: 1px solid rgba(255, 255, 255, 0.1);
          cursor: pointer;
        }

        .cta-button-secondary:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.2);
        }

        /* Bot Selection Section */
        .bot-selection-section {
          padding: 40px 20px 80px;
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
          z-index: 1;
          max-width: 1400px;
          margin: 0 auto;
        }

        .carousel-wrapper {
          display: flex;
          align-items: center;
          gap: 1rem;
          width: 100%;
          max-width: 1400px;
        }

        .carousel-nav {
          background: rgba(255, 184, 77, 0.2);
          border: 2px solid rgba(255, 184, 77, 0.5);
          color: #ffb84d;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          flex-shrink: 0;
        }

        .carousel-nav:hover {
          background: rgba(255, 184, 77, 0.4);
          transform: scale(1.1);
        }

        .carousel-nav i {
          font-size: 1.2rem;
        }

        .bot-selection-container {
          overflow: hidden;
          width: 100%;
          max-width: 1380px;
        }

        .carousel-track {
          display: flex;
          flex-direction: row;
          gap: 1.5rem;
          transition: transform 0.5s cubic-bezier(0.25, 0.1, 0.25, 1);
        }

        .carousel-dots {
          display: flex;
          gap: 0.5rem;
          margin-top: 1.5rem;
        }

        .carousel-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.3);
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .carousel-dot:hover {
          background: rgba(255, 184, 77, 0.6);
        }

        .carousel-dot.active {
          background: #ffb84d;
          transform: scale(1.2);
        }

        .bot-card {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          flex: 0 0 auto;
          width: 320px;
          cursor: pointer;
        }

        .bot-thumbnail {
          position: relative;
          background: rgba(0, 0, 0, 0.5);
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.1);
          width: 100%;
          height: 560px;
          display: flex;
          justify-content: center;
          align-items: center;
          transition: all 0.3s ease;
        }

        .bot-thumbnail-content {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          background: linear-gradient(135deg, rgba(255, 184, 77, 0.05), transparent);
          position: relative;
        }

        .bot-thumbnail-video {
          width: 110%;
          height: 100%;
          object-fit: cover;
          position: absolute;
          top: 0;
          left: 50%;
          transform: translateX(-50%);
          z-index: 1;
        }

        .bot-select-button {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 20px;
          background: rgba(255, 255, 255, 0.02);
          border: 2px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          color: rgba(255, 255, 255, 0.8);
          cursor: pointer;
          transition: all 0.3s ease;
          font-weight: 600;
          font-size: 0.95rem;
          position: relative;
          overflow: hidden;
          width: 100%;
        }

        .bot-select-button::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, var(--bot-color), transparent);
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .bot-select-button:hover {
          transform: translateY(-3px);
          border-color: var(--bot-color);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
        }

        .bot-select-button:hover::before {
          opacity: 0.15;
        }

        .bot-select-button.active {
          background: linear-gradient(135deg, var(--bot-color), transparent);
          border-color: var(--bot-color);
          color: white;
          box-shadow: 0 8px 24px var(--bot-shadow);
        }

        .bot-select-button.active::before {
          opacity: 0.3;
        }

        .bot-select-button i {
          font-size: 1.5rem;
          position: relative;
          z-index: 1;
          color: var(--bot-color);
        }

        .bot-select-button.active i {
          color: white;
        }

        .bot-select-content {
          display: flex;
          flex-direction: column;
          gap: 4px;
          position: relative;
          z-index: 1;
        }

        .bot-select-name {
          font-weight: 700;
          font-size: 1rem;
        }

        .bot-select-description {
          font-size: 0.8rem;
          opacity: 0.8;
        }

        /* Video AI Section - now inside bot-selection-section */
        .video-ai-container {
          position: relative;
          background: rgba(0, 0, 0, 0.5);
          border-radius: 16px;
          overflow: hidden;
          border: 1px solid rgba(255, 184, 77, 0.2);
          box-shadow: 0 20px 60px rgba(255, 184, 77, 0.2);
          flex: 1;
          height: 700px;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .video-container {
          width: 100%;
          height: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .video-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          background: linear-gradient(135deg, rgba(255, 184, 77, 0.05), transparent);
          position: relative;
        }

        .ai-avatar {
          width: 150px;
          height: 150px;
          background: linear-gradient(135deg, #ffb84d, #f59e0b);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1rem;
          position: relative;
          z-index: 2;
        }

        .ai-avatar i {
          font-size: 4rem;
          color: white;
        }

        .start-conversation {
          background: linear-gradient(135deg, #ffb84d, #f59e0b);
          border: none;
          padding: 14px 28px;
          border-radius: 8px;
          color: white;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 1rem;
          margin-top: 1rem;
          position: relative;
          z-index: 2;
          box-shadow: 0 4px 16px rgba(255, 184, 77, 0.4);
        }

        .start-conversation:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 24px rgba(255, 184, 77, 0.6);
        }

        /* Video Popup Modal */
        .video-popup-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: rgba(0, 0, 0, 0.9);
          backdrop-filter: blur(10px);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 2000;
          animation: fadeIn 0.3s ease-out;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .video-popup-modal {
          position: relative;
          width: clamp(320px, 90vw, 1400px);
          height: clamp(400px, 85vh, 900px);
          background: rgba(0, 0, 0, 0.95);
          border-radius: 16px;
          border: 1px solid rgba(255, 184, 77, 0.3);
          box-shadow: 0 30px 80px rgba(255, 184, 77, 0.3);
          overflow: hidden;
          animation: slideIn 0.4s ease-out;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        .video-popup-header {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          padding: 20px;
          background: rgba(0, 0, 0, 0.5);
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          display: flex;
          justify-content: space-between;
          align-items: center;
          z-index: 10;
          backdrop-filter: blur(10px);
        }

        .video-popup-title {
          font-size: 1.5rem;
          font-weight: 600;
          color: #ffb84d;
        }

        .video-popup-close {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 1.2rem;
        }

        .video-popup-close:hover {
          background: rgba(255, 0, 0, 0.2);
          border-color: rgba(255, 0, 0, 0.5);
          transform: scale(1.1);
        }

        .video-popup-content {
          width: 100%;
          height: 100%;
          padding: 80px 20px 20px 20px;
          display: flex;
          gap: 20px;
        }

        .popup-video-section {
          flex: 1;
          background: rgba(0, 0, 0, 0.4);
          border-radius: 12px;
          border: 1px solid rgba(255, 184, 77, 0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
          max-width: 1000px;
          height: 100%;
        }

        .video-container-popup {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .video-placeholder-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
        }

        .placeholder-icon {
          font-size: 4rem;
          margin-bottom: 1rem;
          opacity: 0.5;
        }

        .popup-side-panel {
          flex: 0 0 400px;
          background: rgba(255, 255, 255, 0.02);
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.05);
          padding: 30px;
          display: flex;
          flex-direction: column;
          overflow-y: auto;
        }

        .side-panel-header {
          font-size: 1.5rem;
          font-weight: 600;
          color: white;
          margin-bottom: 20px;
        }

        .side-panel-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .panel-section {
          background: rgba(255, 255, 255, 0.02);
          padding: 20px;
          border-radius: 10px;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }

        .panel-section h4 {
          color: #ffb84d;
          margin-bottom: 10px;
          font-size: 1rem;
        }

        .panel-section p {
          color: rgba(255, 255, 255, 0.7);
          font-size: 0.9rem;
          line-height: 1.6;
          margin: 0;
        }

        .start-video-button {
          background: linear-gradient(135deg, #ffb84d, #f59e0b);
          border: none;
          padding: 14px 24px;
          border-radius: 8px;
          color: white;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 1rem;
          margin-top: auto;
          box-shadow: 0 4px 16px rgba(255, 184, 77, 0.4);
        }

        .start-video-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 24px rgba(255, 184, 77, 0.6);
        }

        /* Responsive for Popup */
        @media (max-width: 900px) {
          .video-popup-content {
            flex-direction: column;
            padding: 70px 15px 15px 15px;
          }

          .popup-video-section {
            height: 60%;
            max-width: 100%;
          }

          .popup-side-panel {
            flex: 0 0 auto;
            height: 40%;
          }
        }

        /* Services Section */
        .services {
          padding: 100px 0;
          position: relative;
          z-index: 1;
        }

        .section-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .section-label {
          font-size: 0.9rem;
          color: #ffb84d;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 1rem;
        }

        .services h2 {
          font-size: 3rem;
          font-weight: 700;
          margin-bottom: 1rem;
          color: #ffffff;
          letter-spacing: -1px;
        }

        .section-subtitle {
          font-size: 1.1rem;
          color: rgba(255, 255, 255, 0.6);
          max-width: 600px;
          margin: 0 auto;
        }

        .services-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 2rem;
        }

        .service-card {
          background: rgba(255, 255, 255, 0.02);
          padding: 2.5rem;
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.05);
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .service-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(255, 184, 77, 0.1), transparent);
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .service-card:hover {
          transform: translateY(-5px);
          border-color: rgba(255, 184, 77, 0.3);
          box-shadow: 0 20px 40px rgba(255, 184, 77, 0.2);
        }

        .service-card:hover::before {
          opacity: 1;
        }

        .service-card i {
          font-size: 2.5rem;
          color: #ffb84d;
          margin-bottom: 1.5rem;
          position: relative;
          z-index: 1;
        }

        .service-card h3 {
          margin-bottom: 1rem;
          color: #ffffff;
          font-size: 1.4rem;
          font-weight: 600;
          position: relative;
          z-index: 1;
        }

        .service-card p {
          color: rgba(255, 255, 255, 0.7);
          line-height: 1.8;
          position: relative;
          z-index: 1;
        }

        /* Workflow Section */
        .workflow-section {
          padding: 100px 0;
          position: relative;
          z-index: 1;
          background: #1a1a1a;
        }

        .workflow-container {
          max-width: 1200px;
          margin: 0 auto;
          position: relative;
        }

        .workflow-step-wrapper {
          display: flex;
          align-items: flex-start;
          margin-bottom: 60px;
          position: relative;
        }

        .workflow-step-wrapper:nth-child(even) {
          flex-direction: row-reverse;
          margin-left: auto;
          width: 70%;
        }

        .workflow-step-wrapper:nth-child(odd) {
          width: 70%;
        }

        .workflow-step-wrapper:last-child {
          width: 80%;
          margin-left: 0;
        }

        .workflow-step {
          background: rgba(255, 255, 255, 0.02);
          padding: 2rem 2rem 2rem 1.5rem;
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          transition: all 0.3s ease;
          display: flex;
          align-items: flex-start;
          gap: 1.5rem;
          flex: 1;
          position: relative;
        }

        .workflow-step:hover {
          border-color: rgba(255, 184, 77, 0.5);
          box-shadow: 0 10px 30px rgba(255, 184, 77, 0.1);
        }

        .workflow-step.highlight {
          background: linear-gradient(135deg, #ffb84d, #f59e0b);
          border-color: #ffb84d;
        }

        .workflow-step.highlight:hover {
          box-shadow: 0 10px 40px rgba(255, 184, 77, 0.3);
        }

        .step-number-badge {
          flex-shrink: 0;
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #ffb84d, #f59e0b);
          clip-path: polygon(30% 0%, 70% 0%, 100% 50%, 70% 100%, 30% 100%, 0% 50%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2.5rem;
          font-weight: 700;
          color: #1a1a1a;
        }

        .workflow-step.highlight .step-number-badge {
          background: white;
        }

        .workflow-step-content {
          flex: 1;
          padding-top: 0.5rem;
        }

        .workflow-step-content h3 {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 1rem;
          color: #ffffff;
        }

        .workflow-step.highlight .workflow-step-content h3 {
          color: #1a1a1a;
        }

        .workflow-step-content p {
          font-size: 1rem;
          line-height: 1.7;
          color: rgba(255, 255, 255, 0.8);
          margin: 0 0 1.5rem 0;
        }

        .workflow-step.highlight .workflow-step-content p {
          color: rgba(0, 0, 0, 0.8);
        }

        .workflow-step-cta {
          display: inline-block;
          padding: 12px 24px;
          background: rgba(0, 0, 0, 0.8);
          color: white;
          text-decoration: none;
          border-radius: 6px;
          font-weight: 600;
          font-size: 0.95rem;
          transition: all 0.3s ease;
          border: none;
          cursor: pointer;
        }

        .workflow-step-cta:hover {
          background: rgba(0, 0, 0, 0.95);
          transform: translateY(-2px);
        }

        /* Responsive for Workflow */
        @media (max-width: 1024px) {
          .workflow-step-wrapper,
          .workflow-step-wrapper:nth-child(even),
          .workflow-step-wrapper:last-child {
            width: 100% !important;
            margin-left: 0 !important;
            flex-direction: row !important;
          }
        }

        @media (max-width: 768px) {
          .workflow-step {
            flex-direction: column;
            align-items: center;
            text-align: center;
          }

          .step-number-badge {
            width: 70px;
            height: 70px;
            font-size: 2rem;
          }
        }

        /* Industry Solutions Section */
        .industry-solutions {
          padding: 100px 0;
          position: relative;
          z-index: 1;
        }

        .industry-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
          max-width: 1200px;
          margin: 0 auto;
        }

        .industry-card {
          background: rgba(255, 255, 255, 0.02);
          padding: 2.5rem;
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.05);
          transition: all 0.3s ease;
          text-align: left;
          position: relative;
          overflow: hidden;
        }

        .industry-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(255, 184, 77, 0.1), transparent);
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .industry-card:hover {
          transform: translateY(-5px);
          border-color: rgba(255, 184, 77, 0.3);
          box-shadow: 0 20px 40px rgba(255, 184, 77, 0.2);
        }

        .industry-card:hover::before {
          opacity: 1;
        }

        .industry-card i {
          font-size: 2.5rem;
          color: #ffb84d;
          margin-bottom: 1.5rem;
          position: relative;
          z-index: 2;
        }

        .industry-card h3 {
          margin-bottom: 1rem;
          color: #ffffff;
          font-size: 1.4rem;
          font-weight: 600;
          position: relative;
          z-index: 2;
        }

        .industry-card p {
          color: rgba(255, 255, 255, 0.7);
          line-height: 1.8;
          position: relative;
          z-index: 2;
          margin-bottom: 1.5rem;
        }

        .industry-features {
          list-style: none;
          padding: 0;
          margin: 0;
          position: relative;
          z-index: 2;
        }

        .industry-features li {
          color: rgba(255, 255, 255, 0.6);
          font-size: 0.9rem;
          margin-bottom: 0.7rem;
          padding-left: 1.5rem;
          position: relative;
        }

        .industry-features li::before {
          content: '✓';
          color: #ffb84d;
          font-weight: bold;
          position: absolute;
          left: 0;
          top: 0;
        }

        /* Contact Section */
        .contact-section {
          padding: 100px 0;
          position: relative;
          z-index: 1;
        }

        .contact-form {
          max-width: 700px;
          margin: 0 auto;
          background: rgba(255, 255, 255, 0.02);
          padding: 3rem;
          border-radius: 16px;
          border: 1px solid rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(10px);
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.5rem;
          margin-bottom: 1.5rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-group label {
          color: rgba(255, 255, 255, 0.8);
          font-weight: 500;
          font-size: 0.9rem;
        }

        .form-group input,
        .form-group textarea {
          padding: 12px 16px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          color: white;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .form-group input:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #ffb84d;
          background: rgba(255, 255, 255, 0.08);
        }

        .form-group input::placeholder,
        .form-group textarea::placeholder {
          color: rgba(255, 255, 255, 0.4);
        }

        .form-group textarea {
          resize: vertical;
          min-height: 120px;
        }

        .form-group.full-width {
          grid-column: 1 / -1;
        }

        .submit-button {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          padding: 14px;
          background: linear-gradient(135deg, #ffb84d, #f59e0b);
          color: white;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 16px rgba(255, 184, 77, 0.4);
          margin-top: 1rem;
        }

        .submit-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 24px rgba(255, 184, 77, 0.6);
        }

        .submit-button:active {
          transform: translateY(0);
        }

        /* Footer */
        .footer {
          padding: 60px 0;
          text-align: center;
          border-top: 1px solid rgba(255, 255, 255, 0.05);
          color: rgba(255, 255, 255, 0.5);
          position: relative;
          z-index: 1;
        }

        .social-links {
          display: flex;
          justify-content: center;
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .social-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 50px;
          height: 50px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          color: rgba(255, 255, 255, 0.7);
          text-decoration: none;
          transition: all 0.3s ease;
          font-size: 1.5rem;
        }

        .social-link:hover {
          background: linear-gradient(135deg, #ffb84d, #f59e0b);
          border-color: #ffb84d;
          color: white;
          transform: translateY(-3px);
          box-shadow: 0 4px 16px rgba(255, 184, 77, 0.4);
        }

        .social-link.instagram:hover {
          background: linear-gradient(135deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888);
        }

        .footer-email {
          color: rgba(255, 255, 255, 0.7);
          text-decoration: none;
          font-size: 1rem;
          margin-bottom: 1rem;
          display: inline-block;
          transition: color 0.3s ease;
        }

        .footer-email:hover {
          color: #ffb84d;
        }

        .footer-email i {
          margin-right: 8px;
          color: #ffb84d;
        }

        /* Responsive */
        @media (max-width: 900px) {
          .hero h1 {
            font-size: 3rem;
          }

          .services h2 {
            font-size: 2.5rem;
          }

          .industry-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .hero h1 {
            font-size: 2.5rem;
          }

          .nav-links {
            display: none;
          }

          .nav-right {
            gap: 1rem;
          }

          .sales-button {
            padding: 10px 20px;
            font-size: 0.85rem;
          }

          .contact-form {
            padding: 2rem;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .cta-buttons {
            flex-direction: column;
            width: 100%;
          }

          .cta-button, .cta-button-secondary {
            width: 100%;
          }
        }
      `}</style>
      <link href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css" rel="stylesheet" />

      <div>
        {/* Header */}
        <header className="header" style={{ background: headerBackground }}>
          <nav className="nav container">
            <div className="logo">
              Pro Avatar Strategy
              <img src={logoImage} alt="Pro Avatar Strategy Logo" className="logo-image" />
            </div>
            <div className="nav-right">
              <ul className="nav-links">
                <li><span className="nav-link" onClick={() => scrollToSection('home')}>{tr.home}</span></li>
                <li><span className="nav-link" onClick={() => scrollToSection('services')}>{tr.services}</span></li>
                <li><span className="nav-link" onClick={() => scrollToSection('industries')}>{tr.industries}</span></li>
                <li><span className="nav-link" onClick={() => scrollToSection('contact')}>{tr.contact}</span></li>
              </ul>
              <button
                className="language-switcher"
                onClick={() => setLanguage(language === 'en' ? 'de' : 'en')}
                aria-label="Switch language"
              >
                <i className="fas fa-globe"></i>
                {language === 'en' ? 'DE' : 'EN'}
              </button>
              <a
                href="https://www.instagram.com/proavatarstrategy/"
                target="_blank"
                rel="noopener noreferrer"
                className="header-social-link"
                aria-label="Follow us on Instagram"
              >
                <i className="fab fa-instagram"></i>
              </a>
              <button className="sales-button" onClick={() => scrollToSection('contact')}>
                <i className="fas fa-handshake"></i>
                {tr.getInTouch}
              </button>
            </div>
          </nav>
        </header>

        <main>
          {/* Hero Section */}
          <section id="home" className="hero">
            <div className="container">
              <div className="hero-badge">
                <i className="fas fa-sparkles"></i> {tr.heroBadge}
              </div>
              <h1 dangerouslySetInnerHTML={{ __html: tr.heroTitle.replace('Through', 'Through<br/>') }}></h1>
              <p>{tr.heroSubtitle}</p>
              <div className="cta-buttons">
                <button className="cta-button" onClick={() => scrollToSection('bot-selection')}>
                  <i className="fas fa-robot"></i> {tr.talkToOlivio}
                </button>
                <button className="cta-button-secondary" onClick={() => scrollToSection('services')}>
                  {tr.viewServices}
                </button>
              </div>
            </div>
          </section>

          {/* Bot Selection Section with Video */}
          <section id="bot-selection" className="bot-selection-section">
            <div className="carousel-wrapper">
              <button
                className="carousel-nav carousel-nav-prev"
                onClick={() => setCarouselIndex(prev => (prev - 1 + botsList.length) % botsList.length)}
              >
                <i className="fas fa-chevron-left"></i>
              </button>

              <div className="bot-selection-container">
                <div
                  className="carousel-track"
                  style={{
                    transform: `translateX(-${carouselIndex * 344}px)`
                  }}
                >
                  {/* Render all bots plus duplicates for infinite scroll */}
                  {[...botsList, ...botsList].map((bot, i) => (
                    <div
                      key={`${bot.id}-${i}`}
                      className={`bot-card ${selectedBot === bot.id ? 'active' : ''}`}
                      style={{
                        '--bot-color': bot.color,
                        '--bot-shadow': `0 8px 24px ${bot.color}40`
                      }}
                      onClick={() => {
                        setSelectedBot(bot.id);
                        startConversation();
                      }}
                    >
                      <div
                        className="bot-thumbnail"
                        onMouseEnter={(e) => {
                          const video = e.currentTarget.querySelector('video');
                          if (video) video.play();
                        }}
                        onMouseLeave={(e) => {
                          const video = e.currentTarget.querySelector('video');
                          if (video) {
                            video.pause();
                            video.currentTime = 0;
                          }
                        }}
                      >
                        <div className="bot-thumbnail-content">
                          <video
                            className="bot-thumbnail-video"
                            loop
                            muted
                            playsInline
                            preload="metadata"
                            poster={bot.poster || undefined}
                          >
                            <source src={bot.video} type="video/mp4" />
                          </video>
                        </div>
                      </div>
                      <button
                        className={`bot-select-button ${selectedBot === bot.id ? 'active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedBot(bot.id);
                          startConversation();
                        }}
                      >
                        <i className={`fas ${bot.icon}`}></i>
                        <div className="bot-select-content">
                          <div className="bot-select-name">{bot.name}</div>
                          <div className="bot-select-description">{bot.description}</div>
                        </div>
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <button
                className="carousel-nav carousel-nav-next"
                onClick={() => setCarouselIndex(prev => (prev + 1) % botsList.length)}
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>

            <div className="carousel-dots">
              {botsList.map((bot, index) => (
                <button
                  key={bot.id}
                  className={`carousel-dot ${index === carouselIndex ? 'active' : ''}`}
                  onClick={() => setCarouselIndex(index)}
                />
              ))}
            </div>
          </section>

          {/* Video Popup Modal */}
          {showVideoPopup && (
            <VideoPopup
              onClose={closeVideoPopup}
              onStartVideoChat={startVideoChat}
              translations={tr}
              botConfig={bots[selectedBot]?.configName || 'olivio_sales'}
              botInfo={bots[selectedBot] || bots.olivio}
            />
          )}

          {/* Services Section */}
          <section id="services" className="services">
            <div className="container">
              <div className="section-header">
                <div className="section-label">{tr.ourServices}</div>
                <h2 dangerouslySetInnerHTML={{ __html: tr.servicesTitle.replace('Your', 'Your<br/>') }}></h2>
                <p className="section-subtitle">{tr.servicesSubtitle}</p>
              </div>
              <div className="services-grid">
                <div className="service-card">
                  <i className="fas fa-robot"></i>
                  <h3>{tr.customAI}</h3>
                  <p>{tr.customAIDesc}</p>
                </div>
                <div className="service-card">
                  <i className="fas fa-video"></i>
                  <h3>{tr.videoAI}</h3>
                  <p>{tr.videoAIDesc}</p>
                </div>
                <div className="service-card">
                  <i className="fas fa-cogs"></i>
                  <h3>{tr.aiOrchestration}</h3>
                  <p>{tr.aiOrchestrationDesc}</p>
                </div>
                <div className="service-card">
                  <i className="fas fa-chart-line"></i>
                  <h3>{tr.aiAnalytics}</h3>
                  <p>{tr.aiAnalyticsDesc}</p>
                </div>
                <div className="service-card">
                  <i className="fas fa-shield-alt"></i>
                  <h3>{tr.enterpriseSecurity}</h3>
                  <p>{tr.enterpriseSecurityDesc}</p>
                </div>
                <div className="service-card">
                  <i className="fas fa-headset"></i>
                  <h3>{tr.support247}</h3>
                  <p>{tr.support247Desc}</p>
                </div>
              </div>
            </div>
          </section>

          {/* Workflow Section */}
          <section id="workflow" className="workflow-section">
            <div className="container">
              <div className="section-header">
                <div className="section-label">{tr.howWeWork}</div>
                <h2>{tr.workflowTitle}</h2>
              </div>

              <div className="workflow-container">
                <div className="workflow-step-wrapper">
                  <div className="workflow-step">
                    <div className="step-number-badge">1</div>
                    <div className="workflow-step-content">
                      <h3>{tr.workflowStep1Title}</h3>
                      <p>{tr.workflowStep1Desc}</p>
                    </div>
                  </div>
                </div>

                <div className="workflow-step-wrapper">
                  <div className="workflow-step">
                    <div className="step-number-badge">2</div>
                    <div className="workflow-step-content">
                      <h3>{tr.workflowStep2Title}</h3>
                      <p>{tr.workflowStep2Desc}</p>
                    </div>
                  </div>
                </div>

                <div className="workflow-step-wrapper">
                  <div className="workflow-step">
                    <div className="step-number-badge">3</div>
                    <div className="workflow-step-content">
                      <h3>{tr.workflowStep3Title}</h3>
                      <p>{tr.workflowStep3Desc}</p>
                    </div>
                  </div>
                </div>

                <div className="workflow-step-wrapper">
                  <div className="workflow-step">
                    <div className="step-number-badge">4</div>
                    <div className="workflow-step-content">
                      <h3>{tr.workflowStep4Title}</h3>
                      <p>{tr.workflowStep4Desc}</p>
                    </div>
                  </div>
                </div>

                <div className="workflow-step-wrapper">
                  <div className="workflow-step highlight">
                    <div className="step-number-badge">5</div>
                    <div className="workflow-step-content">
                      <h3>{tr.workflowStep5Title}</h3>
                      <p>{tr.workflowStep5Desc}</p>
                      <button className="workflow-step-cta" onClick={() => scrollToSection('contact')}>
                        {tr.integrateAI}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Industry Solutions Section */}
          <section id="industries" className="industry-solutions">
            <div className="container">
              <div className="section-header">
                <div className="section-label">{tr.industrySolutions}</div>
                <h2>{tr.industryTitle}</h2>
                <p className="section-subtitle">{tr.industrySubtitle}</p>
              </div>

              <div className="industry-grid">
                <div className="industry-card">
                  <i className="fas fa-home"></i>
                  <h3>{tr.realEstate}</h3>
                  <p>{tr.realEstateDesc}</p>
                  <ul className="industry-features">
                    <li>{tr.realEstateFeature1}</li>
                    <li>{tr.realEstateFeature2}</li>
                    <li>{tr.realEstateFeature3}</li>
                    <li>{tr.realEstateFeature4}</li>
                  </ul>
                </div>

                <div className="industry-card">
                  <i className="fas fa-shopping-cart"></i>
                  <h3>{tr.ecommerce}</h3>
                  <p>{tr.ecommerceDesc}</p>
                  <ul className="industry-features">
                    <li>{tr.ecommerceFeature1}</li>
                    <li>{tr.ecommerceFeature2}</li>
                    <li>{tr.ecommerceFeature3}</li>
                    <li>{tr.ecommerceFeature4}</li>
                  </ul>
                </div>

                <div className="industry-card">
                  <i className="fas fa-video"></i>
                  <h3>{tr.influencer}</h3>
                  <p>{tr.influencerDesc}</p>
                  <ul className="industry-features">
                    <li>{tr.influencerFeature1}</li>
                    <li>{tr.influencerFeature2}</li>
                    <li>{tr.influencerFeature3}</li>
                    <li>{tr.influencerFeature4}</li>
                  </ul>
                </div>

                <div className="industry-card">
                  <i className="fas fa-headset"></i>
                  <h3>{tr.customerService}</h3>
                  <p>{tr.customerServiceDesc}</p>
                  <ul className="industry-features">
                    <li>{tr.customerServiceFeature1}</li>
                    <li>{tr.customerServiceFeature2}</li>
                    <li>{tr.customerServiceFeature3}</li>
                    <li>{tr.customerServiceFeature4}</li>
                  </ul>
                </div>

                <div className="industry-card">
                  <i className="fas fa-heartbeat"></i>
                  <h3>{tr.healthcare}</h3>
                  <p>{tr.healthcareDesc}</p>
                  <ul className="industry-features">
                    <li>{tr.healthcareFeature1}</li>
                    <li>{tr.healthcareFeature2}</li>
                    <li>{tr.healthcareFeature3}</li>
                    <li>{tr.healthcareFeature4}</li>
                  </ul>
                </div>

                <div className="industry-card">
                  <i className="fas fa-bed"></i>
                  <h3>{tr.hospitality}</h3>
                  <p>{tr.hospitalityDesc}</p>
                  <ul className="industry-features">
                    <li>{tr.hospitalityFeature1}</li>
                    <li>{tr.hospitalityFeature2}</li>
                    <li>{tr.hospitalityFeature3}</li>
                    <li>{tr.hospitalityFeature4}</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section id="contact" className="contact-section">
            <div className="container">
              <div className="section-header">
                <div className="section-label">{tr.contactLabel}</div>
                <h2>{tr.contactTitle}</h2>
                <p className="section-subtitle">{tr.contactSubtitle}</p>
                <p className="section-subtitle" style={{ marginTop: '1rem' }}>
                  <a href="mailto:contact@pro-avatar-strategy.com" style={{ color: '#ffb84d', textDecoration: 'none' }}>
                    <i className="fas fa-envelope" style={{ marginRight: '8px' }}></i>
                    contact@pro-avatar-strategy.com
                  </a>
                </p>
              </div>

              <div className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">{tr.fullName} *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={contactForm.name}
                      onChange={handleContactInputChange}
                      placeholder={tr.namePlaceholder}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">{tr.emailAddress} *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={contactForm.email}
                      onChange={handleContactInputChange}
                      placeholder={tr.emailPlaceholder}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="company">{tr.company}</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={contactForm.company}
                      onChange={handleContactInputChange}
                      placeholder={tr.companyPlaceholder}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">{tr.phoneNumber}</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={contactForm.phone}
                      onChange={handleContactInputChange}
                      placeholder={tr.phonePlaceholder}
                    />
                  </div>
                </div>

                <div className="form-group full-width">
                  <label htmlFor="message">{tr.message} *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={contactForm.message}
                    onChange={handleContactInputChange}
                    placeholder={tr.messagePlaceholder}
                  ></textarea>
                </div>

                <button
                  type="button"
                  className="submit-button"
                  onClick={handleContactSubmit}
                  disabled={isSubmitting}
                  style={{
                    opacity: isSubmitting ? 0.7 : 1,
                    cursor: isSubmitting ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i> {tr.sending}
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane"></i> {tr.sendMessage}
                    </>
                  )}
                </button>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="footer">
          <div className="container">
            <div className="social-links">
              <a
                href="https://www.instagram.com/proavatarstrategy/"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link instagram"
                aria-label="Follow us on Instagram"
              >
                <i className="fab fa-instagram"></i>
              </a>
            </div>
            <a href="mailto:contact@pro-avatar-strategy.com" className="footer-email">
              <i className="fas fa-envelope"></i>
              contact@pro-avatar-strategy.com
            </a>
            <p>&copy; 2025 Pro Avatar Strategy. {tr.allRightsReserved}</p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default OlivioAIAgency;
